import { Cog8ToothIcon } from "@heroicons/react/24/outline"
import { useState, useEffect, useRef } from 'react'

const VIEW_OPTIONS = [
    { id: 'sales_last_30', label: '30D' },
    { id: 'sales_last_90', label: '90D' },
    { id: 'sales_last_365', label: '365D' },
    { id: 'sales_daily_avg', label: 'Daily AVG' }
]

const DEFAULT_STATE = VIEW_OPTIONS.reduce((acc, option) => ({
    ...acc,
    [option.id]: true
}), {})

export const InventorySettings = ({ onVisibilityChange, STORAGE_KEY = 'inventory-columns-settings' }) => {
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef(null)
    const [dropdownStyles, setDropdownStyles] = useState(null)
    const [selectedOptions, setSelectedOptions] = useState(() => {
        const saved = localStorage.getItem(STORAGE_KEY)
        const initialState = saved ? JSON.parse(saved) : DEFAULT_STATE
        if (onVisibilityChange) {
            setTimeout(() => onVisibilityChange(initialState), 0)
        }
        return initialState
    })

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedOptions))
        if (onVisibilityChange) {
            onVisibilityChange(selectedOptions)
        }
    }, [selectedOptions, onVisibilityChange])

    const updateDropdownPosition = () => {
        if (dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect()
            const viewportWidth = window.innerWidth
            
            // Get the actual scroll position
            const scrollX = window.pageXOffset || document.documentElement.scrollLeft
            
            // Calculate position relative to viewport
            const right = viewportWidth - rect.right + scrollX
            
            setDropdownStyles({
                position: 'fixed',
                top: rect.bottom + 5,
                right: right - 15, // Add small offset
                zIndex: 50
            })
        }
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
            document.addEventListener('scroll', updateDropdownPosition, { passive: true })
            window.addEventListener('resize', updateDropdownPosition)
            updateDropdownPosition()
        }
        updateDropdownPosition()
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('scroll', updateDropdownPosition)
            window.removeEventListener('resize', updateDropdownPosition)
        }
    }, [isOpen])

    const handleOptionChange = (optionId) => {
        setSelectedOptions(prev => ({
            ...prev,
            [optionId]: !prev[optionId]
        }))
    }

    const handleSelectAll = () => {
        const allChecked = Object.values(selectedOptions).every(value => value)
        const newState = VIEW_OPTIONS.reduce((acc, option) => ({
            ...acc,
            [option.id]: !allChecked
        }), {})
        setSelectedOptions(newState)
    }

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <Cog8ToothIcon 
                className="h-4 w-4 cursor-pointer" 
                onClick={() => setIsOpen(!isOpen)}
            />
            
            {isOpen && (
                <div style={dropdownStyles} className="w-48 bg-white text-black rounded-md shadow-lg border border-gray-200 p-2">
                    <div className="flex items-center justify-between border-b pb-1 mb-1">
                        <p className="text-sm font-bold">Sales Columns</p>
                        <label className="flex items-center hover:bg-gray-50 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={Object.values(selectedOptions).every(value => value)}
                                onChange={handleSelectAll}
                                className="mr-1 h-3 w-3"
                            />
                            <span className="text-xs">All</span>
                        </label>
                    </div>
                    <div>
                        {VIEW_OPTIONS.map((option) => (
                            <label key={option.id} className="flex items-center py-1 hover:bg-gray-50 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={!!selectedOptions[option.id]}
                                    onChange={() => handleOptionChange(option.id)}
                                    className="mr-2 h-3 w-3"
                                />
                                <span className="text-sm">{option.label}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}